import { urlUtil } from '@cmg/common';
import { Box } from '@cmg/design-system';
import defaultTo from 'lodash/defaultTo';
import React from 'react';

import { SidePanelTitle } from '../../components/side-bar/components/design-system';
import { useCmgEntityKeyContext } from '../../contexts/CmgEntityKeyContext';
import { useSupersededOfferingContext } from '../../contexts/SupersededOfferingContext';
import {
  useOfferingSidePanelOfferingId,
  useOfferingSidePanelSearch,
} from '../../hooks/useOfferingSidePanelMatch';
import { getAttestationStatus } from '../compliance/context/hooks/certificates/Certificates.model';
import {
  OrderBookPersonaType,
  useIdentifyOrderBookPersona,
} from '../hooks/useIdentifyOrderBookPersona';
import IndicationComplianceLoadingSkeleton from './compliance/IndicationComplianceLoadingSkeleton';
import IndicationComplianceWarningWrapper from './compliance/IndicationComplianceWarningWrapper';
import IndicationFormEmptyState from './components/indication-form-empty-state/IndicationFormEmptyState';
import ComplianceErrorBanner from './components/manage-indication/common/compliance-required-banner/ComplianceErrorBanner';
import {
  IndicationActivitySalesAndTradingContextProvider,
  useIndicationActivitySalesAndTradingContext,
} from './context/IndicationActivitySalesAndTradingContext';
import {
  IndicationActivitySyndicateContextProvider,
  useIndicationActivitySyndicateContext,
} from './context/IndicationActivitySyndicateContext';
import IndicationActivitySalesAndTrading from './IndicationActivitySalesAndTrading';
import IndicationActivitySyndicate from './IndicationActivitySyndicate';

export type IndicationActivitySellSideProps = {
  displayMissingContacts: boolean;
  setDisplayMissingContacts: (val: boolean) => void;
};

const IndicationActivitySyndicateComponent: React.FC<IndicationActivitySellSideProps> = ({
  displayMissingContacts,
  setDisplayMissingContacts,
}) => {
  const {
    complianceWarning,
    loading,
    basicPublishedOffering,
    attestation,
    certificateType,
    complianceWarning: { hasComplianceWarning },
    orderBookConfiguration,
  } = useIndicationActivitySyndicateContext();
  const indication = basicPublishedOffering?.orderBook.institutionalIndication;

  React.useEffect(() => {
    if (!basicPublishedOffering) {
      setDisplayMissingContacts(false);
    }
  }, [basicPublishedOffering, setDisplayMissingContacts]);

  if (loading) {
    return <IndicationComplianceLoadingSkeleton />;
  }

  return (
    <Box>
      {!!basicPublishedOffering && (
        <ComplianceErrorBanner
          offeringType={defaultTo(basicPublishedOffering?.type, null)}
          isMissingContacts={displayMissingContacts}
          attestationStatus={hasComplianceWarning ? getAttestationStatus(attestation) : undefined}
          prospectusDeliveryStatus={
            indication?.investorProspectusDeliveryStatus?.overallDeliveryStatus
          }
          interestLevels={indication?.interestLevels ?? []}
          isLimitIndicationsOnlyEnabled={orderBookConfiguration.isLimitIndicationsOnlyEnabled}
          certificateType={certificateType}
          isIndicationPriceAsPercentageParEnabled={
            orderBookConfiguration.isIndicationPriceAsPercentageParEnabled
          }
          isOnlySharesUnitOnIndicationsEnabled={
            orderBookConfiguration.isOnlySharesUnitOnIndicationsEnabled
          }
        />
      )}
      <IndicationComplianceWarningWrapper
        complianceWarning={complianceWarning}
        indicationExists={!!basicPublishedOffering?.orderBook?.institutionalIndication}
        loading={loading}
        attestation={attestation}
        certificateType={certificateType}
      >
        <IndicationActivitySyndicate
          displayMissingContacts={displayMissingContacts}
          setDisplayMissingContacts={setDisplayMissingContacts}
        />
      </IndicationComplianceWarningWrapper>
    </Box>
  );
};

const IndicationActivitySalesAndTradingComponent: React.FC<IndicationActivitySellSideProps> = ({
  displayMissingContacts,
  setDisplayMissingContacts,
}) => {
  const {
    complianceWarning,
    loading,
    coveredIndication,
    attestation,
    certificateType,
    orderBookConfiguration,
    basicPublishedOffering,
  } = useIndicationActivitySalesAndTradingContext();

  React.useEffect(() => {
    if (!basicPublishedOffering) {
      setDisplayMissingContacts(false);
    }
  }, [basicPublishedOffering, setDisplayMissingContacts]);

  return (
    <Box>
      {!!basicPublishedOffering && (
        <ComplianceErrorBanner
          offeringType={defaultTo(basicPublishedOffering?.type, null)}
          isMissingContacts={displayMissingContacts}
          attestationStatus={
            complianceWarning?.hasComplianceWarning ? getAttestationStatus(attestation) : undefined
          }
          prospectusDeliveryStatus={coveredIndication?.prospectusStatus?.overallDeliveryStatus}
          interestLevels={coveredIndication?.interestLevels ?? []}
          isLimitIndicationsOnlyEnabled={orderBookConfiguration.isLimitIndicationsOnlyEnabled}
          certificateType={certificateType}
          isIndicationPriceAsPercentageParEnabled={
            orderBookConfiguration.isIndicationPriceAsPercentageParEnabled
          }
          isOnlySharesUnitOnIndicationsEnabled={
            orderBookConfiguration.isOnlySharesUnitOnIndicationsEnabled
          }
        />
      )}
      <IndicationComplianceWarningWrapper
        complianceWarning={complianceWarning}
        indicationExists={!!coveredIndication}
        loading={loading}
        attestation={attestation}
        certificateType={certificateType}
      >
        <IndicationActivitySalesAndTrading
          displayMissingContacts={displayMissingContacts}
          setDisplayMissingContacts={setDisplayMissingContacts}
        />
      </IndicationComplianceWarningWrapper>
    </Box>
  );
};

const IndicationActivitySellSideRoute: React.FC = () => {
  const { cmgEntityKey } = useCmgEntityKeyContext();
  const offeringId = useOfferingSidePanelOfferingId();
  const { type } = useIdentifyOrderBookPersona({ offeringId });
  const search = useOfferingSidePanelSearch();
  const indicationId = urlUtil.queryParse<{ indicationId: string }>(search)?.indicationId;
  const { isFiledOffering } = useSupersededOfferingContext();
  const [displayMissingContacts, setDisplayMissingContacts] = React.useState(false);

  if (type === OrderBookPersonaType.SYNDICATE) {
    return (
      <IndicationActivitySyndicateContextProvider
        offeringId={offeringId}
        cmgEntityKey={cmgEntityKey}
        indicationId={indicationId}
      >
        <SidePanelTitle>Communicated Demand</SidePanelTitle>
        {!isFiledOffering ? (
          <IndicationActivitySyndicateComponent
            displayMissingContacts={displayMissingContacts}
            setDisplayMissingContacts={setDisplayMissingContacts}
          />
        ) : (
          <IndicationFormEmptyState />
        )}
      </IndicationActivitySyndicateContextProvider>
    );
  }

  if (type === OrderBookPersonaType.SALES_AND_TRADING) {
    return (
      <IndicationActivitySalesAndTradingContextProvider
        offeringId={offeringId}
        cmgEntityKey={cmgEntityKey}
        indicationId={indicationId}
      >
        <SidePanelTitle>Communicated Demand</SidePanelTitle>
        {!isFiledOffering ? (
          <IndicationActivitySalesAndTradingComponent
            displayMissingContacts={displayMissingContacts}
            setDisplayMissingContacts={setDisplayMissingContacts}
          />
        ) : (
          <IndicationFormEmptyState />
        )}
      </IndicationActivitySalesAndTradingContextProvider>
    );
  }

  return null;
};

export default IndicationActivitySellSideRoute;
